// console.debug(`${new Date().toISOString()} ${performance.now().toFixed(2)}ms [${import.meta.url}]`)

declare const marcaDiaPuro: unique symbol

/**
 * Un `Date` del que el compilador sabe que está a 00:00 UTC (día puro).
 *
 * La marca es sólo de tipos: en runtime sigue siendo un `Date` normal. Lo que
 * aporta es que un `Date` cualquiera ya NO es asignable donde se espera un
 * `UTCDate`, así que olvidarse de normalizar deja de compilar en vez de
 * descubrirse en una aserción a mitad de un cálculo.
 *
 * Sólo se crean `UTCDate` por estas puertas: `fromString`, `fromLocalDate`,
 * `createDate`, `todayUTC`, `firstNext` y la aritmética de este módulo. Para un
 * `Date` que llega de fuera (una librería, un JSON ya hidratado), `asUTCDate`
 * lo comprueba en runtime y lo marca.
 *
 * Al revés sí funciona: un `UTCDate` es un `Date`, así que se puede pasar a
 * cualquier cosa que espere `Date` (formateadores, gráficos) sin ceremonia.
 */
export type UTCDate = Date & { readonly [marcaDiaPuro]: true }

/** El único sitio del código donde se marca. Privado a propósito. */
function marcar(dateUTC: Date): UTCDate {
    return dateUTC as UTCDate
}

/**
 * Normaliza una fecha local (reloj de pared del usuario) a 00:00 UTC.
 * Normalizar fechas locales → usar get*
 *
 * NO es idempotente y no puede serlo: lee campos LOCALES, así que aplicarla a
 * una fecha que ya es día puro UTC la desplaza un día con offset negativo. Se
 * llama `fromLocalDate` justamente para que no se aplique dos veces.
 */
export function fromLocalDate(dateLocal: Date): UTCDate {
    return marcar(
        new Date(Date.UTC(dateLocal.getFullYear(), dateLocal.getMonth(), dateLocal.getDate()))
    )
}

/**
 * Create UTC-date with monthZero. ==> no really need (directly use: new Date(Date.UTC(year, monthZero, day)))
 */
export function createDate(year: number, monthZero: number, day: number): UTCDate {
    return marcar(new Date(Date.UTC(year, monthZero, day)))
}

/**
 * Deserializar from 'normal' date string to UTC-date: '2026-07-10' -> Date.UTC(2026, 6, 10)
 */
// export function fromDateString(ymd: string): Date {
//     const [y, m, d] = ymd.split('-').map(Number)
//     return new Date(Date.UTC(y, m - 1, d))
// }
export function fromString(ymd: string): UTCDate {
    const datePart = ymd.split('T')[0]
    const [y, m, d] = datePart.split('-').map(Number)
    const fecha = new Date(Date.UTC(y, m - 1, d))

    // `Date.UTC` no se queja de nada: '2026-13-45' desborda a 2027-02-14 y un
    // año de dos dígitos se mapea a 19xx. Comprobar el viaje de vuelta rechaza
    // ambos, y de paso el texto que no es una fecha (y/m/d quedan undefined).
    if (fecha.getUTCFullYear() !== y || fecha.getUTCMonth() !== m - 1 || fecha.getUTCDate() !== d) {
        throw new Error(`Fecha inválida: ${ymd}`)
    }
    return marcar(fecha)
}

/**
 * Serializar from UTC-date to 'normal' date string: Date.UTC(2026, 6, 10) -> '2026-07-10'
 * Operar y serializar day‑only → usar getUTC*
 */
export function toYMD(dateUTC: UTCDate): string {
    assertIsUTCDate(dateUTC)
    const y = dateUTC.getUTCFullYear()
    const m = dateUTC.getUTCMonth() + 1
    const d = dateUTC.getUTCDate()

    const mm = m < 10 ? `0${m}` : `${m}`
    const dd = d < 10 ? `0${d}` : `${d}`

    return `${y}-${mm}-${dd}`
}

/** Reconstruye un UTCDate desde un timestamp (ms). Lanza si no es un día UTC puro. */
export function fromTime(time: number): UTCDate {
    const d = new Date(time)
    assertIsUTCDate(d) // cubre NaN/Infinity (Invalid Date) y horas ≠ 00:00:00.000 UTC
    return marcar(d)
}

export function toTime(d: UTCDate): number {
    return d.getTime()
}

/**
 * Primer día del mes siguiente.
 * Operar y serializar day‑only → usar getUTC*
 */
export function firstNextUTC(dateUTC: UTCDate): UTCDate {
    assertIsUTCDate(dateUTC)
    return marcar(new Date(Date.UTC(dateUTC.getUTCFullYear(), dateUTC.getUTCMonth() + 1, 1)))
}

/**
 * Fecha mínima del sistema.
 */
export const MIN_DATE_UTC = fromString('1970-01-01')

/**
 * Hoy (día local del usuario) como día puro UTC.
 *
 * Función y no constante: una constante se evalúa al importar el módulo, así
 * que en una sesión que cruza la medianoche «hoy» se queda en ayer para
 * siempre — y de ahí pasa al sello de `storage.setJson` y a las puertas de
 * frescura de euribor/IRS, que dejan de refrescar.
 */
export function todayUTC(): UTCDate {
    return fromLocalDate(new Date())
}

/**
 * First of next month (first posible effective date).
 * Función por el mismo motivo que `todayUTC`.
 */
export function firstNext(): UTCDate {
    return firstNextUTC(todayUTC())
}

/**
 * Add months preservando end-of-month.
 * Operar y serializar day‑only → usar getUTC*
 */
export function addMonths(dateUTC: UTCDate, n: number): UTCDate {
    assertIsUTCDate(dateUTC)
    const y = dateUTC.getUTCFullYear()
    const m = dateUTC.getUTCMonth()
    const d = dateUTC.getUTCDate()
    const res = new Date(Date.UTC(y, m + n, d))
    // Si el mes cambió por overflow, ajustar al último día del mes anterior
    if (res.getUTCDate() !== d) {
        res.setUTCDate(0) // último día del mes anterior
    }
    return marcar(res)
}

/**
 * Add days.
 * Operar y serializar day‑only → usar getUTC*
 */
export function addDays(dateUTC: UTCDate, n: number): UTCDate {
    assertIsUTCDate(dateUTC)
    return marcar(
        new Date(
            Date.UTC(dateUTC.getUTCFullYear(), dateUTC.getUTCMonth(), dateUTC.getUTCDate() + n)
        )
    )
}

/**
 * Add years.
 * Operar y serializar day‑only → usar getUTC*
 */
export function addYears(dateUTC: UTCDate, n: number): UTCDate {
    assertIsUTCDate(dateUTC)
    return marcar(
        new Date(
            Date.UTC(dateUTC.getUTCFullYear() + n, dateUTC.getUTCMonth(), dateUTC.getUTCDate())
        )
    )
}

/**
 * Year fraction (ACT/365.25). SIN USO — comentada, no borrada.
 *
 * Aquí todo cae a primero de mes y las fracciones de año son múltiplos de 1/12,
 * así que lo coherente sería `diffMonths(d1, d2) / 12`, no ACT/365.25: con esta
 * convención un año natural da 0,999316 en vez de 1, y el desajuste se arrastra
 * a cualquier descuento. Por eso no basta con descomentarla — si algún día hace
 * falta una fracción de año, escríbela sobre `diffMonths`.
 *
 * (El cuerpo ya no llamaba a `fromLocalDate`: los argumentos vienen asertados
 * como día puro y volver a normalizar desplazaba un día con offset negativo.)
 */
// export function yearFraction(d1_UTC: UTCDate, d2_UTC: UTCDate): number {
//     assertIsUTCDate(d1_UTC)
//     assertIsUTCDate(d2_UTC)
//     const diffDays = (d2_UTC.getTime() - d1_UTC.getTime()) / 86400000
//     return diffDays / 365.25
// }

/**
 * Diff months (determinista).
 * Operar y serializar day‑only → usar getUTC*
 */
export function diffMonths(d1_UTC: UTCDate, d2_UTC: UTCDate): number {
    assertIsUTCDate(d1_UTC)
    assertIsUTCDate(d2_UTC)
    const y1 = d1_UTC.getUTCFullYear()
    const m1 = d1_UTC.getUTCMonth()
    const y2 = d2_UTC.getUTCFullYear()
    const m2 = d2_UTC.getUTCMonth()

    return (y2 - y1) * 12 + (m2 - m1)
}

/**
 * Diff days (determinista, day‑only).
 * d2_UTC - d1_UTC
 */
export function diffDays(d1_UTC: UTCDate, d2_UTC: UTCDate): number {
    assertIsUTCDate(d1_UTC)
    assertIsUTCDate(d2_UTC)
    const t1 = d1_UTC.getTime() // siempre 00:00 UTC
    const t2 = d2_UTC.getTime() // siempre 00:00 UTC

    return (t2 - t1) / 86400000 // 24 * 60 * 60 * 1000
}

/**
 * Verifica que una fecha es realmente un day-only UTC-midnight.
 */
export function isUTCDate(date: Date): date is UTCDate {
    return (
        date.getUTCHours() === 0 &&
        date.getUTCMinutes() === 0 &&
        date.getUTCSeconds() === 0 &&
        date.getUTCMilliseconds() === 0
    )
}

/**
 * Puerta de entrada para un `Date` que viene de fuera y debería ser día puro:
 * lo comprueba en runtime y lo marca. Si no lo es, lanza.
 *
 * Es el único camino legítimo de `Date` a `UTCDate` fuera de este módulo; si
 * te ves escribiendo `as UTCDate`, usa esto en su lugar.
 *
 * Devuelve una copia, como el resto de las puertas. La aserción sólo mira el
 * `Date` una vez, y los `Date` son mutables: marcando el del llamante, quien
 * conservara la referencia original podía romper el día puro después, y ya no
 * queda ninguna comprobación aguas abajo que lo note.
 */
export function asUTCDate(date: Date): UTCDate {
    assertIsUTCDate(date)
    return marcar(new Date(date.getTime()))
}

export function assertIsUTCDate(date: Date): void {
    if (typeof date === 'string') throw new Error(`No UTCDate pero string: ${date}`)

    // Lanza error si date NO es un objeto Date
    if (!(date instanceof Date)) {
        throw new Error(`No UTCDate: ${date}`)
    }
    // Antes que nada: sobre un Invalid Date, el `toISOString()` de abajo lanza
    // `RangeError: Invalid time value` y se pierde el motivo real.
    if (Number.isNaN(date.getTime())) {
        throw new Error(`No UTCDate: fecha inválida (${String(date)})`)
    }
    if (
        date.getUTCHours() !== 0 ||
        date.getUTCMinutes() !== 0 ||
        date.getUTCSeconds() !== 0 ||
        date.getUTCMilliseconds() !== 0
    ) {
        throw new Error(`No UTCDate: ${date.toISOString()}`)
    }
}

// ****************************************************************************
// ****** Both dates must already be normalized to UTC 00:00. *****************
// ****************************************************************************

/**
 * Deterministic day-only equality.
 * Both dates must already be normalized to UTC 00:00.
 * Directamente d1 === d2 of d1 !== d2 comparan instancias!!
 */
export function eqDay(a: UTCDate, b: UTCDate): boolean {
    return a.getTime() === b.getTime()
}

export function neqDay(a: UTCDate, b: UTCDate): boolean {
    return a.getTime() !== b.getTime()
}

/**
 * Deterministic day-only less-than.
 * Both dates must already be normalized to UTC 00:00.
 * También funcionan: <, <=, >, >= (se transforman en: d1.getTime() <operant> d2.getTime())
 */
export function ltDay(a: UTCDate, b: UTCDate): boolean {
    return a.getTime() < b.getTime()
}

/**
 * Deterministic day-only greater-than.
 */
export function gtDay(a: UTCDate, b: UTCDate): boolean {
    return a.getTime() > b.getTime()
}

/**
 * Deterministic day-only less-or-equal.
 */
export function leDay(a: UTCDate, b: UTCDate): boolean {
    return a.getTime() <= b.getTime()
}

/**
 * Deterministic day-only greater-or-equal.
 */
export function geDay(a: UTCDate, b: UTCDate): boolean {
    return a.getTime() >= b.getTime()
}
